/**
 * Which customers a platform account may work on: every one, or a list.
 * Opened from the user-administration table; saves through the admin API.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { api, ApiError, type AdminCustomer, type AdminUser } from "../lib/api";

export function UserCustomersDialog({
  user,
  customers,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  customers: AdminCustomer[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [all, setAll] = useState(user.all_customers);
  const [chosen, setChosen] = useState<Set<string>>(new Set(user.customer_ids));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const toggle = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function save() {
    setBusy(true);
    setError("");
    try {
      await api.adminSetUserCustomers(user.id, { all_customers: all, customer_ids: [...chosen] });
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("admin.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("customers.assignTitle", { name: user.full_name })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          {user.role === "sysadmin" ? t("customers.assignSysadmin") : t("customers.assignBody")}
        </DialogContentText>
        <FormControlLabel
          control={
            <Checkbox
              checked={all}
              disabled={user.role === "sysadmin"}
              onChange={(e) => setAll(e.target.checked)}
            />
          }
          label={t("customers.assignAll")}
        />
        <List dense disablePadding>
          {customers.map((c) => (
            <ListItem key={c.id} disablePadding>
              <ListItemButton
                disabled={all || user.role === "sysadmin"}
                onClick={() => toggle(c.id)}
                dense
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" size="small" checked={all || chosen.has(c.id)} tabIndex={-1} />
                </ListItemIcon>
                <ListItemText
                  primary={c.name}
                  secondary={c.status === "inactive" ? t("customers.status.inactive") : c.code}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={busy || user.role === "sysadmin"}
        >
          {t("customers.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
